import { TOPBTN, TOPBTN_WARN } from "#puf/constants.ts";
import { store } from "#puf/store/index.ts";
import { useSelector } from "@xstate/store-react";

/**
 * Human-in-the-loop apply panel for AI-generated state-machine proposals.
 *
 * Renders only when `pendingProposal` is set (the AI assistant just called
 * `propose_state_machine`). Shows the proposed character, rationale, and a
 * compact diff summary (states + frame counts, transitions, initial state).
 *
 * Apply dispatches `proposal.apply`, which runs the same `machine.scaffold`
 * reducer in a single undoable transaction. Dismiss clears the proposal
 * without touching the project.
 *
 * The plan calls for a graph preview beside the existing canvas; we ship a
 * list view first because the proposal lives at a different scope from
 * MachineCanvas (which renders the *current* machine + layout from the
 * store) — wiring a second xyflow instance just for preview is a meaningful
 * diff for a panel the user spends seconds on. List view is greppable,
 * keyboard-accessible, and cheap to iterate later.
 */
export function ProposalPanel() {
  const pending = useSelector(store, (s) => s.context.pendingProposal);
  if (!pending) return null;

  const { proposal, rationale } = pending;
  const stateCount = proposal.states.length;
  const transitionCount = proposal.transitions?.length ?? 0;
  const totalFrames = proposal.states.reduce((acc, s) => acc + s.frameCount, 0);

  function onApply() {
    store.trigger["proposal.apply"]();
  }

  function onDismiss() {
    store.trigger["proposal.clear"]();
  }

  return (
    <div className="border-b border-accent/40 bg-accent/8 px-3 py-3">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <p className="text-[10px] font-semibold uppercase tracking-[1.4px] text-dim">Proposed by AI · preview</p>
          <p className="text-sm font-semibold text-text">{proposal.character}</p>
          <p className="text-[11px] text-dim">
            {String(stateCount)} state{stateCount === 1 ? "" : "s"} · {String(transitionCount)} transition
            {transitionCount === 1 ? "" : "s"} · {String(totalFrames)} frame{totalFrames === 1 ? "" : "s"} · initial
            {" "}
            <span className="font-mono text-text">{proposal.initial}</span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button onClick={onApply} className={TOPBTN} type="button">
            Apply
          </button>
          <button onClick={onDismiss} className={TOPBTN_WARN} type="button">
            Dismiss
          </button>
        </div>
      </div>

      {rationale.trim().length > 0 && <p className="mb-2 text-[11px] leading-5 text-dim">{rationale}</p>}

      <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
        <section className="rounded-sm border border-line bg-bg-3 px-2 py-2">
          <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-[1.2px] text-dim">States</h4>
          <ul className="space-y-0.5 font-mono text-[11px] text-text">
            {proposal.states.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2">
                <span>
                  {s.id === proposal.initial && <span className="mr-1 text-accent">●</span>}
                  {s.id}
                </span>
                <span className="text-dim">
                  {String(s.frameCount)} frame{s.frameCount === 1 ? "" : "s"}
                  {s.loop ? ` · ${s.loop}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-sm border border-line bg-bg-3 px-2 py-2">
          <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-[1.2px] text-dim">Transitions</h4>
          {transitionCount === 0
            ? <p className="text-[11px] text-dim">(none)</p>
            : (
              <ul className="space-y-0.5 font-mono text-[11px] text-text">
                {proposal.transitions?.map((t, i) => (
                  <li key={`${t.from}-${t.event}-${t.to}-${String(i)}`} className="truncate">
                    <span>{t.from}</span>
                    <span className="text-dim">→</span>
                    <span>{t.to}</span>
                    <span className="text-dim">on</span>
                    <span className="text-accent">{t.event}</span>
                  </li>
                ))}
              </ul>
            )}
        </section>
      </div>
    </div>
  );
}
